import classNames from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '@swf/core/store';
import type { EventId, MarketId } from '@swf/generated/types/api/sports';
import { MarketTemplate, Title } from '@swf/sports/components';
import { ExpandablePanel } from '@swf/sports/components/ExpandablePanel/ExpandablePanel';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import type { MarketLevelTemplateId } from '@swf/sports/types/templates';

import type { TemplateViewsGroupedByDate, TemplateViewsGroupedByMarket } from '../../model/types';
import { isAntePostTemplateViewGroupedByDate } from '../../utils/isAntePostTemplateViewGroupedByDate';
import DatePanel from '../DatePanel/DatePanel';

import styles from './EventPanel.scss';

interface Props {
  eventGroup: TemplateViewsGroupedByMarket | TemplateViewsGroupedByDate;
  eventId?: EventId;
  marketId?: MarketId;
}

const EventPanel: React.FC<Props> = ({ eventGroup, eventId, marketId }) => {
  const { widgetSelect } = useEventMarketTemplatesWidget();
  const market = useSelector((state: RootState) => widgetSelect.market(state, marketId ?? ''));
  const event = useSelector((state: RootState) =>
    widgetSelect.event(state, market ? market.eventId : eventId ?? -1)
  );

  const isGroupedByDate = isAntePostTemplateViewGroupedByDate(eventGroup);

  return (
    <div
      className={classNames(styles.panel, {
        [styles.shadow]: !isGroupedByDate
      })}
      data-testid="ante-post-event-panel"
    >
      <ExpandablePanel openInitially title={<Title>{event.eventName}</Title>}>
        {isGroupedByDate
          ? eventGroup.map((dateGroup) => (
              <DatePanel
                key={`${dateGroup[0].template.marketId}_date_group`}
                dateGroup={dateGroup}
              />
            ))
          : eventGroup.map(({ template }) => (
              <MarketTemplate
                key={template.marketId}
                {...template}
                templateId={template.templateId as MarketLevelTemplateId}
              />
            ))}
      </ExpandablePanel>
    </div>
  );
};

export default EventPanel;
